<template>
  <div class="job-post-settings-container">
    <JobPostSettingsSkeleton v-if="loading" />

    <div
      v-else
      class="form-wrapper"
    >
      <section class="settings-section">
        <h2 class="text-h2 section-title mb-4">
          My Team Page Access
        </h2>
        <p class="section-description mb-4">
          Control who can access the invite page and add new users to the account.
          <a
            href="#"
            class="section-description-link"
            data-beacon-article-sidebar="69cd28049207e139315c3eb2"
            @click.stop
          >Learn more here</a>.
        </p>
        <DpplSlimSwitch
          v-model="isInviteAdminOnlyEnabled"
          :label="isInviteAdminOnlyEnabled ? 'Admins Only' : 'Everyone'"
          active-color="brand"
          :disabled="saving"
          a11y-label-text="Toggle invite by admins only"
          @update:model-value="save"
        />
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import DpplSlimSwitch from '@/components/datapeople/DpplSlimSwitch.vue';
import JobPostSettingsSkeleton from '@/components/settings/components/JobPostSettingsSkeleton.vue';
import { getProjectEditableFeatureFlags, updateProjectEditableFeatureFlags } from '@/api/settings';
import { useAppSnackbar } from '@/stores/app/snackbar';

const { addSuccessSnackbar, addErrorSnackbar } = useAppSnackbar();

const isInviteAdminOnlyEnabled = ref(false);

const saving = ref(false);
const loading = ref(true);

const initialInviteAdminOnly = ref(false);

const isDirty = computed(() => (
  isInviteAdminOnlyEnabled.value !== initialInviteAdminOnly.value
));

const save = async () => {
  if (saving.value) return;
  if (!isDirty.value) return;

  saving.value = true;

  try {
    await updateProjectEditableFeatureFlags({
      isInviteAdminOnlyEnabled: isInviteAdminOnlyEnabled.value,
    });

    initialInviteAdminOnly.value = isInviteAdminOnlyEnabled.value;

    addSuccessSnackbar({ text: 'Job Post settings have been updated' });
  } catch (err) {
    addErrorSnackbar({ text: err?.message || 'Failed to update Job Post settings' });
  } finally {
    saving.value = false;
  }
};

const loadJobPostSettings = async () => {
  loading.value = true;

  try {
    const data = await getProjectEditableFeatureFlags();

    isInviteAdminOnlyEnabled.value = data?.isInviteAdminOnlyEnabled || false;

    initialInviteAdminOnly.value = isInviteAdminOnlyEnabled.value;
  } catch (err) {
    addErrorSnackbar({ text: err?.message || 'Failed to load Job Post settings' });
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadJobPostSettings();
});
</script>

<style scoped lang="less">
.job-post-settings-container {
  width: 100%;
}

.form-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  border-radius: 10px;
  background: @white;
  padding: 28px 40px 40px;
}

.settings-section {
  width: 100%;
}

.section-title {
  color: @text-darkest;
}

.section-description {
  max-width: 100%;
  font-size: 16px;
  font-weight: 500 !important;
  line-height: 150%;
  color: @text-dark;
}

.section-description-link {
  color: @text-brand;
  font-weight: 500 !important;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    opacity: 0.8;
  }
}

.load-error {
  color: @error;
}
</style>
